import * as React from "react";

import { Button, IconButton, Input, Select, Tooltip } from "Components/ui";
import { Search, X } from "Components/ui/icons";
import { toDateInputValue } from "../data";
import { ACTION_TYPE_OPTIONS, RANGE_OPTIONS, RESOURCE_OPTIONS, type AuditLogsData } from "../types";

export interface AuditLogFiltersProps {
  data: AuditLogsData;
  /** Right-aligned controls at the end of the row, e.g. an Export button. */
  trailing?: React.ReactNode;
}

/** Toolbar row: search, resource, action type, actor and date range. */
export const AuditLogFilters: React.FC<AuditLogFiltersProps> = ({ data, trailing }) => {
  const { filters, setFilter } = data;
  const today = toDateInputValue(new Date());
  const resourceSelected = filters.resource !== "all";

  const actionSelect = (
    <Select
      options={ACTION_TYPE_OPTIONS}
      value={filters.actionType}
      onValueChange={(value) => setFilter("actionType", value)}
      disabled={resourceSelected}
      aria-label="Action type"
      className="w-full sm:w-36"
    />
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input
        value={filters.search}
        onChange={(event) => setFilter("search", event.target.value)}
        placeholder="Search actor, action or id…"
        aria-label="Search audit logs"
        leading={<Search />}
        trailing={
          filters.search ? (
            <IconButton
              label="Clear search"
              icon={<X aria-hidden="true" />}
              size="sm"
              hideTooltip
              onClick={() => setFilter("search", "")}
            />
          ) : undefined
        }
        wrapperClassName="w-full sm:w-72"
      />
      <Select
        options={RESOURCE_OPTIONS}
        value={filters.resource}
        onValueChange={(value) => setFilter("resource", value)}
        aria-label="Resource"
        className="w-full sm:w-48"
      />
      {resourceSelected ? (
        <Tooltip content="Clear the resource filter to filter by action type.">
          <span className="w-full sm:w-auto">{actionSelect}</span>
        </Tooltip>
      ) : (
        actionSelect
      )}
      <Select
        options={data.actorOptions}
        value={filters.actor}
        onValueChange={(value) => setFilter("actor", value)}
        aria-label="Actor"
        className="w-full sm:w-44"
      />
      <Select
        options={RANGE_OPTIONS}
        value={filters.range}
        onValueChange={(value) => setFilter("range", value)}
        aria-label="Date range"
        className="w-full sm:w-40"
      />
      {filters.range === "custom" && (
        <>
          <Input
            type="date"
            value={filters.from}
            max={filters.to || today}
            onChange={(event) => setFilter("from", event.target.value)}
            aria-label="From date"
            wrapperClassName="w-full sm:w-40"
          />
          <Input
            type="date"
            value={filters.to}
            min={filters.from || undefined}
            max={today}
            onChange={(event) => setFilter("to", event.target.value)}
            aria-label="To date"
            wrapperClassName="w-full sm:w-40"
          />
        </>
      )}
      {data.hasActiveFilters && (
        <Button variant="ghost" leftIcon={<X />} onClick={data.clearFilters}>
          Clear filters
        </Button>
      )}
      {trailing && <div className="flex items-center gap-2 sm:ml-auto">{trailing}</div>}
    </div>
  );
};
